import React, { useState } from 'react';
import { RefreshCcw, Loader2 } from 'lucide-react';

/**
 * "Force refresh" button — wipes every layer of cache that could be holding
 * old code or stale data, then hard-reloads.
 *
 * What it does, in order:
 *   1. Unregisters every Service Worker
 *   2. Deletes every Cache Storage entry
 *   3. Clears IndexedDB caches that could hold stale library content
 *   4. Hard-reloads with cache bypass
 *
 * Mounted in the AppShell footer so any user can self-heal without
 * needing dev tools or shell commands.
 */
const ForceRefreshButton = ({ variant = 'compact' }) => {
  const [busy, setBusy] = useState(false);

  const purgeAndReload = async () => {
    setBusy(true);
    try {
      // 1. Service workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      // 2. Cache Storage
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
      // 3. IndexedDB caches that could hold stale library/content rows
      if ('indexedDB' in window && indexedDB.databases) {
        try {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs
              .filter((db) => db.name && /smarter|library|offline/i.test(db.name))
              .map((db) => new Promise((res) => {
                const req = indexedDB.deleteDatabase(db.name);
                req.onsuccess = req.onerror = req.onblocked = () => res();
              }))
          );
        } catch {
          /* deleteDatabase isn't critical */
        }
      }
    } finally {
      // 4. Hard reload — append a cache-buster so even an aggressive
      // intermediary (Cloudflare edge, ISP transparent cache) is forced
      // to ask origin for a fresh HTML shell.
      const u = new URL(window.location.href);
      u.searchParams.set('_t', String(Date.now()));
      window.location.href = u.toString();
    }
  };

  if (variant === 'full') {
    return (
      <button
        type="button"
        onClick={purgeAndReload}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-amber-100 bg-gradient-to-br from-amber-600/40 via-orange-600/30 to-rose-600/30 border border-amber-500/50 hover:from-amber-600/60 hover:to-rose-600/50 rounded-lg px-4 py-3 transition-colors disabled:opacity-60"
        title="Wipes service worker + cached files + offline data, then reloads. Use this if the app looks stuck on old features."
        data-testid="force-refresh-app-full"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <RefreshCcw className="w-4 h-4" />
        )}
        {busy ? 'Refreshing…' : 'Force update app'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={purgeAndReload}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-400 hover:text-white px-2.5 py-1 rounded-lg border border-slate-700/60 bg-slate-800/40 hover:bg-slate-700/60 transition-colors disabled:opacity-60"
      title="Clear cached app + service-worker and reload from server. Use this if anything looks stale or broken."
      data-testid="force-refresh-app"
    >
      {busy ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <RefreshCcw className="w-3 h-3" />
      )}
      Force refresh
    </button>
  );
};

export default ForceRefreshButton;
